import {
  type ComponentProps, type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { ImportMethod } from '~/_shared/types/importMethod/importMethod';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { geocodingStartTracking } from '~/store/geocoding/geocoding.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { usePrimarySpreadsheetRealSpreadsheets } from '~/store/selectors/usePrimarySpreadsheetRealSpreadsheets';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import { ImportDataComponent } from './importData.component';
import { useImportFileData } from './useImportFileData';
import { useImportTextData } from './useImportTextData';

export type ImportDataContainerProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  initialImportMethod?: ImportMethod;
}>;

export const ImportDataContainer: FC<ImportDataContainerProps> = ({
  isOpen,
  onClose,
  initialImportMethod,
}: ImportDataContainerProps) => {
  const clientId = useClientIdSelector();
  const userId = useUserIdSelector();
  const spreadsheetId = usePrimarySpreadsheetId();
  const realSpreadsheets = usePrimarySpreadsheetRealSpreadsheets();
  const mapId = useMapIdSelector();
  const dispatch = useDispatch();

  const { replaceCurrentModal: replaceCurrentModalWithImportDataModal } = useModal(ModalType.ImportData);
  const { importFileData, isLoading: isFileImportLoading, errorMessage: errorFileImport, clearErrorMessage: clearFileImportError } = useImportFileData();
  const { importTextData, isLoading: isTextImportLoading, errorMessage: errorTextImport, clearErrorMessage: clearTextImportError } = useImportTextData();

  const onImportSuccess = useCallback((importMethod: ImportMethod) => {
    if (importMethod === ImportMethod.Append && realSpreadsheets?.[0]) {
      dispatch(geocodingStartTracking({
        isPaused: false,
        progress: 0,
        realSpreadsheetId: realSpreadsheets[0].id,
        triggeredByUserId: userId,
      }));
    }

    onClose();
  }, [dispatch, onClose, realSpreadsheets, userId]);

  const onSubmitFileImport: ComponentProps<typeof ImportDataComponent>['onSubmitFileImport'] = useCallback((
    importMethod,
    file,
    onProgressChange,
    onStatusChange,
  ) => {
    if (!clientId || !userId || !mapId || !spreadsheetId) {
      return;
    }

    importFileData({
      clientId, userId, mapId, spreadsheetId, importMethod, file, onProgressChange, onStatusChange, onSuccess: () => onImportSuccess(importMethod),
    });
  }, [clientId, importFileData, mapId, onImportSuccess, spreadsheetId, userId]);

  const onSubmitTextInputImport: ComponentProps<typeof ImportDataComponent>['onSubmitTextInputImport'] = useCallback((
    importMethod,
    input,
  ) => {
    if (!clientId || !userId || !mapId || !spreadsheetId) {
      return;
    }

    importTextData({
      clientId, userId, mapId, spreadsheetId, importMethod, input, onSuccess: () => onImportSuccess(importMethod),
    });
  }, [clientId, importTextData, mapId, onImportSuccess, spreadsheetId, userId]);

  const onReplaceImportMethodSelect = useCallback(() => {
    replaceCurrentModalWithImportDataModal({
      initialImportMethod: ImportMethod.Replace,
    });
  }, [replaceCurrentModalWithImportDataModal]);

  return (
    <ImportDataComponent
      isOpen={isOpen}
      onClose={onClose}
      onSubmitFileImport={onSubmitFileImport}
      onSubmitTextInputImport={onSubmitTextInputImport}
      initialImportMethod={initialImportMethod}
      isLoading={isFileImportLoading || isTextImportLoading}
      errorFileImport={errorFileImport}
      errorTextImport={errorTextImport}
      clearFileImportError={clearFileImportError}
      clearTextImportError={clearTextImportError}
      onReplaceImportMethodSelect={onReplaceImportMethodSelect}
    />
  );
};
